import React from 'react';
import {
  StatsigProvider,
  useClientAsyncInit,
  useStatsigClient,
  StatsigClient,
  StatsigPlugin,
  Experiment,
} from '@statsig/react-bindings';
import { currentUser } from 'js/utils/user';
import { UseExperimentOptions } from '@statsig/react-bindings/src/useExperiment';

const statsigClientApiKey = () =>
  window.$rails_env === 'production'
    ? 'client-tqGw3FVZkcgeRvE0x0aVZdDk6AdWdRMOM6NCzEGIJot'
    : 'client-tblDvtlzgbpOP0nNzudKyDqahJztNmFs9bkYC0ersEc';
export const withStatsigClient =
  <Props extends object>(
    Component: React.ComponentType<Props>,
    customUserProps?:
      | Record<string, any>
      | ((props: Props) => Record<string, any>),
    plugins: StatsigPlugin<StatsigClient>[] = [],
  ): React.FC<Props> =>
  (props) => {
    // If customUserProps is a function, call it with props to get the custom properties
    const userProps =
      typeof customUserProps === 'function'
        ? customUserProps(props)
        : customUserProps || {};

    const { client } = useClientAsyncInit(
      statsigClientApiKey(),
      {
        userID: (currentUser?.id ?? '').toString(),
        custom: userProps,
      },
      {
        environment: { tier: window.$rails_env },
        plugins,
      },
    );
    return (
      <StatsigProvider client={client} loadingComponent={<div />}>
        <Component {...props} />
      </StatsigProvider>
    );
  };

export const useStatsigParam = <T,>(
  store: string,
  param: string,
  defaultValue: T,
) => {
  const { client } = useStatsigClient();
  const result = {
    getRaw: () => client.getParameterStore(store).get<T>(param, defaultValue),
    getUnescaped: () => {
      if (typeof defaultValue === 'string') {
        return unescapeHtmlEntities(result.getRaw() as string);
      }
      return result.getRaw();
    },
  };

  return result;
};

const unescapeHtmlEntities = (str: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, 'text/html');
  return doc.body.textContent ?? '';
};

/**
 * A wrapper around useExperiment that provides some type safety between variants' JSON values
 * @param defaults - Determines the types for parameters.
 *    If the experiment is not enabled in the dashboard, or if `bucketUser` is false, these default values will be used.
 */
export function useStatsigExperiment<
  GroupJSONSchema extends Record<string, unknown> = Record<string, unknown>,
>(
  experimentName: string,
  defaults: GroupJSONSchema,
  bucketUser: boolean,
  opts?: UseExperimentOptions,
) {
  const { client } = useStatsigClient();
  let experiment: Experiment | undefined = undefined;

  // Don't bucket the user unless we need to
  if (bucketUser) {
    experiment = client.getExperiment(experimentName, opts);
  }

  return {
    experiment,
    name: experiment?.name,
    getParam: function getParam<P extends keyof GroupJSONSchema>(parameter: P) {
      if (experiment === undefined || !bucketUser) {
        return defaults[parameter];
      }
      return (
        (experiment.value as GroupJSONSchema)[parameter] ?? defaults[parameter]
      );
    },
  };
}
